import React from 'react';
import { MapPin, Phone, Clock, Navigation, MessageCircle, ShieldCheck, Car, Train } from 'lucide-react';
import { CLINIC_CONFIG } from '../utils/whatsapp';
import { SpecialistAvatar } from './SpecialistAvatar';

interface LocationSectionProps {
  onOpenLeadModal?: (title?: string) => void;
}

export const LocationSection: React.FC<LocationSectionProps> = ({ onOpenLeadModal }) => {
  return (
    <section id="iletisim" className="py-12 sm:py-16 bg-white border-t border-slate-200 relative">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-900 text-xs font-bold shadow-2xs">
            <MapPin className="w-3.5 h-3.5 text-amber-600" />
            <span>Merkezi & Kolay Ulaşım</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Nilüfer FSM Bulvarı <span className="text-amber-600">Kliniğimiz & İletişim</span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            Bursa'nın kalbinde, Fatih Sultan Mehmet Bulvarı üzerinde metro ve kapalı otopark avantajıyla konforlu randevu deneyimi.
          </p>
        </div>

        {/* Grid: Details & Map Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8 items-stretch">
          
          {/* Contact Details Card (5 Cols on lg, 5 Cols on xl) */}
          <div className="lg:col-span-5 bg-slate-50 rounded-3xl border border-slate-200 p-6 sm:p-8 flex flex-col justify-between space-y-6 shadow-2xs">
            
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Açık Adres</h3>
                  <p className="text-xs sm:text-sm text-slate-700 mt-1 font-medium">
                    {CLINIC_CONFIG.address}
                  </p>
                  <p className="text-[11px] text-amber-800 font-semibold mt-0.5">
                    (FSM Bulvarı Doktor Kliniği Bünyesinde)
                  </p>
                </div>
              </div>

              {/* Transportation & Parking info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="bg-white p-3.5 rounded-2xl border border-slate-200 text-xs shadow-2xs">
                  <div className="flex items-center gap-1.5 font-bold text-slate-900 mb-1">
                    <Train className="w-3.5 h-3.5 text-amber-600" />
                    <span>Metro İle Ulaşım</span>
                  </div>
                  <p className="text-[11px] text-slate-600">
                    FSM Metro İstasyonu'na sadece 3 dakika yürüme mesafesinde.
                  </p>
                </div>

                <div className="bg-white p-3.5 rounded-2xl border border-slate-200 text-xs shadow-2xs">
                  <div className="flex items-center gap-1.5 font-bold text-slate-900 mb-1">
                    <Car className="w-3.5 h-3.5 text-amber-600" />
                    <span>Otopark İmkanı</span>
                  </div>
                  <p className="text-[11px] text-slate-600">
                    Bina önü ve çevresinde kolay araç park alanı mevcuttur.
                  </p>
                </div>
              </div>

              {/* Appointment Only Notice */}
              <div className="flex items-start gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Randevu & Seans Düzeni</h3>
                  <p className="text-xs sm:text-sm text-amber-900 font-bold mt-1">
                    Yalnızca Randevu İle Hizmet Verilmektedir
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Ön görüşme, kıl analizi ve seanslarımız için lütfen önceden randevu oluşturunuz.
                  </p>
                </div>
              </div>

              {/* Phone & Direct Lines */}
              <div className="flex items-start gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Klinik Telefonu & Randevu</h3>
                  <a
                    href={`tel:${CLINIC_CONFIG.landlineRaw}`}
                    className="text-sm sm:text-base text-slate-900 hover:text-amber-700 font-extrabold mt-0.5 block transition-colors"
                  >
                    {CLINIC_CONFIG.landlineDisplay}
                  </a>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Canlı Danışma & Destek: <span className="text-emerald-700 font-bold">Çevrim İçi & Aktif</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Direct CTA Buttons */}
            <div className="pt-4 border-t border-slate-200 space-y-2.5">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.openLiveChat) {
                    window.openLiveChat();
                  } else if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('open-live-chat'));
                  }
                }}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-105 text-slate-950 font-black text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm cursor-pointer transition-all hover:scale-[1.01]"
              >
                <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                <span>Uzmanımız ile Canlı Desteğe Bağlan & Bilgi Al</span>
              </button>

              <a
                href={CLINIC_CONFIG.googleMapsUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full py-2.5 px-4 rounded-xl bg-white hover:bg-slate-100 text-slate-800 font-bold text-xs flex items-center justify-center gap-2 border border-slate-300 transition-colors"
              >
                <Navigation className="w-3.5 h-3.5 text-amber-600" />
                <span>Google Haritalar'da Aç</span>
              </a>
            </div>

          </div>

          {/* Embedded Google Maps Card (7 Cols) */}
          <div className="lg:col-span-7 bg-slate-900 rounded-3xl border border-slate-200 lg:border-slate-800 overflow-hidden relative flex flex-col justify-between min-h-[420px] sm:min-h-[480px] shadow-lg">
            
            {/* Google Maps Responsive Embed */}
            <div className="relative w-full flex-1 min-h-[360px] sm:min-h-[420px] bg-slate-950">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2379.1523085229487!2d28.97817873445076!3d40.23067632943958!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14ca1446cd7b311d%3A0x655ef232658d6d24!2sFethiye%2C%20Ferhat%20Sk%2C%2016140%20Ni%CC%87l%C3%BCfer%2FBursa!5e0!3m2!1str!2str!4v1789508023389!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title="Bursa Nilüfer FSM Bulvarı Erkek Medikal Lazer Kliniği Harita Konumu"
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>

            {/* Bottom bar inside map card */}
            <div className="p-3.5 sm:p-4 bg-slate-950 border-t border-slate-800 text-xs text-slate-300 flex flex-wrap items-center justify-between gap-3 z-10">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="font-semibold text-white text-[11px] sm:text-xs">
                  Nilüfer / Bursa • FSM Bulvarı & Fethiye Lokasyonu
                </span>
              </div>

              <a
                href="https://maps.google.com/?q=Fethiye,+Ferhat+Sk,+16140+Nil%C3%BCfer/Bursa"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] sm:text-xs font-bold transition-all shadow-sm"
              >
                <Navigation className="w-3.5 h-3.5" />
                <span>Yol Tarifi Al</span>
              </a>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
